import { HotKeyAction } from "../../data/HotKeyAction";
import { EditorModel } from "../../staticModels/EditorModel";
import { LabelType } from "../../data/enums/LabelType";
import { EditorData } from "../../data/EditorData";
import { EditorActions } from "../../logic/actions/EditorActions";
import { PolygonRenderEngine } from "../../logic/render/PolygonRenderEngine";
import { PolylineRenderEngine } from "../../logic/render/PolylineRenderEngine";
import { LineRenderEngine } from "../../logic/render/LineRenderEngine";
import { LabelActions } from "../../logic/actions/LabelActions";
import { ImageActions } from "../../logic/actions/ImageActions";
import { ViewPortActions } from "../../logic/actions/ViewPortActions";
import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import { updateImageDragModeStatus } from "../../store/general/actionCreators";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { store } from "../..";
import {
  redo,
  undo,
} from "../../views/project/label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";
import { RectRenderEngine } from "../../logic/render/RectRenderEngine";
import { UpdateImageList } from "../../logic/actions/annotationActions";
import { BaseRenderEngine } from "../../logic/render/BaseRenderEngine";
import { DEFAULT_HOTKEYS, mergeHotkeysPreservingLabelCombos, withLabelHotkeys } from "./hotkeysConfig";

var hideAll = true;

function isEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  return !!target?.closest?.("input, textarea, [contenteditable]");
}

function finishDrawingFromHotkey(event: KeyboardEvent): void {
  if (isEditableTarget(event)) return;

  const engine = EditorModel.supportRenderingEngine;
  if (!engine || engine.labelType === LabelType.RECT) {
    return;
  }

  if (!engine.isInProgress()) {
    return;
  }

  event.preventDefault();
  const editorData: EditorData = EditorActions.getEditorData();

  switch (engine.labelType) {
    case LabelType.POLYGON:
      (engine as PolygonRenderEngine).addLabelAndFinishCreation(editorData);
      break;
    case LabelType.POLYLINE:
      (engine as PolylineRenderEngine).addLabelAndFinishCreation(editorData);
      break;
    case LabelType.LINE:
      (engine as LineRenderEngine).finishDrawingFromHotkey(editorData);
      break;
    default:
      return;
  }

  EditorActions.fullRender();
}

function cancelDrawingFromHotkey(event: KeyboardEvent): void {
  if (isEditableTarget(event)) return;

  const engine = EditorModel.supportRenderingEngine;
  if (!engine || !engine.isInProgress()) {
    return;
  }

  event.preventDefault();

  switch (engine.labelType) {
    case LabelType.POLYGON:
      (engine as PolygonRenderEngine).cancelLabelCreation();
      break;
    case LabelType.POLYLINE:
      (engine as PolylineRenderEngine).cancelLabelCreation();
      break;
    case LabelType.LINE:
      (engine as LineRenderEngine).cancelLabelCreation();
      break;
    case LabelType.RECT:
      (engine as RectRenderEngine).cancelLabelCreation();
      break;
    default:
      return;
  }

  EditorActions.fullRender();
}

export default function editorActionsHelper(hotKeys) {
  const labelNames = LabelsSelector.getLabelNames() || [];
  const merged = mergeHotkeysPreservingLabelCombos(hotKeys);

  // When class labels are in Redux, rebuild label slots from that list only.
  // When not yet loaded, keep whatever label slots the caller already configured
  // (ImagesDropZone builds them from result.labels) — never invent a default 30.
  hotKeys =
    labelNames.length > 0 ? withLabelHotkeys(merged, labelNames) : merged;

  const deleteLabelAction = (event: KeyboardEvent) => {
    if (isEditableTarget(event)) return;
    event.preventDefault();
    LabelActions.deleteActiveLabel();
  };

  const actions: HotKeyAction[] = Object.keys(hotKeys).reduce((a, c) => {
    const action = getActions(c, hotKeys[c], deleteLabelAction);
    action && a.push(action);
    return a;
  }, [] as HotKeyAction[]);

  if (!actions.some((entry) => entry.keyCombo.join("+").toLowerCase() === "backspace")) {
    actions.push({
      keyCombo: ["Backspace"],
      action: deleteLabelAction,
    });
  }

  const redoAction = getActions("Redo", hotKeys.Redo ?? DEFAULT_HOTKEYS.Redo);
  if (redoAction) {
    const hasShiftRedo = actions.some(
      (entry) =>
        entry.keyCombo.join("+").toLowerCase() === "control+shift+z"
    );
    if (!hasShiftRedo) {
      actions.push({
        keyCombo: ["Control", "Shift", "z"],
        action: redoAction.action,
      });
    }
  }

  return actions;
}

function getActions(key, value, deleteLabelAction?: (event: KeyboardEvent) => void) {
  switch (key) {
    case "Finish Drawing":
    case "Enter":
      return {
        keyCombo: value ?? ["Enter"],
        action: finishDrawingFromHotkey,
      };

    case "Cancel Drawing":
    case "Escape":
      return {
        keyCombo: value ?? ["Escape"],
        action: cancelDrawingFromHotkey,
      };

    case "Delete Label":
    case "Delete":
      return {
        keyCombo: value,
        action: deleteLabelAction,
      };

    case "Hide Selected":
    case "Hide":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          event.stopPropagation();
          LabelActions.hideUnHideActiveLabel();
        },
      };

    case "Pan":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          if (GeneralSelector.getImageDragModeStatus()) {
            return;
          }
          event.preventDefault();
          store.dispatch(updateImageDragModeStatus(true));
        },
      };

    case "Next Image":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          const currentImageIndex: number = LabelsSelector.getActiveImageIndex();
          const nextIndex = currentImageIndex + 1;
          if (nextIndex < LabelsSelector.getImagesData().length) {
            UpdateImageList(nextIndex);
          }
        },
      };

    case "Prev Image":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          const currentImageIndex: number = LabelsSelector.getActiveImageIndex();
          const prevIndex = currentImageIndex - 1;
          if (prevIndex >= 0) {
            UpdateImageList(prevIndex);
          }
        },
      };

    case "Copy":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;

          const engine = EditorModel.supportRenderingEngine as any;
          if (!engine || typeof engine.copyHandler !== "function") {
            return;
          }

          try {
            event.preventDefault();
            engine.copyHandler();
          } catch (err) {
            console.error("copyHandler failed:", err);
          }
        },
      };

    case "Paste":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;

          const activeLabelType = LabelsSelector.getActiveLabelType();
          if (!BaseRenderEngine.canPasteInto(activeLabelType)) {
            return;
          }

          event.preventDefault();
          const engine = EditorModel.supportRenderingEngine;
          if (engine && typeof engine.pasteHandler === "function") {
            engine.pasteHandler();
          }
        },
      };

    case "Draw":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          if (!GeneralSelector.getImageDragModeStatus()) {
            return;
          }
          event.preventDefault();
          store.dispatch(updateImageDragModeStatus(false));
        },
      };

    case "Save":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          const target = event.target as HTMLElement | null;
          if (target && target.closest?.("input, textarea, [contenteditable]")) {
            return;
          }

          event.preventDefault();
          const saveButton = document.querySelector(
            'button[id="auto-save"]'
          ) as HTMLButtonElement | null;
          if (saveButton) {
            saveButton.click();
          }
        },
      };

    case "Undo":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          undo();
        },
      };

    case "Redo":
    case "Redo Shift":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          redo();
        },
      };

    case "Zoom In":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          ViewPortActions.zoomIn();
        },
      };

    case "Zoom Out":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          ViewPortActions.zoomOut();
        },
      };

    case "Hide All":
      return {
        keyCombo: value,
        action: (event: KeyboardEvent) => {
          if (isEditableTarget(event)) return;
          event.preventDefault();
          event.stopPropagation();
          LabelActions.hideLabelAll((hideAll = !hideAll));
        },
      };

    default:
      if (key.includes("label")) {
        return {
          keyCombo: value,
          action: (event: KeyboardEvent) => {
            if (isEditableTarget(event)) return;
            event.preventDefault();
            ImageActions.setActiveLabelOnActiveImage(
              parseInt(key.split(" ")[1], 10)
            );
            EditorActions.fullRender();
          },
        };
      }

      return null;
  }
}
